
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PACKAGES, COUNTRIES, CATEGORIES } from '../data';
import { Package } from '../types';

// --- Sub-components for Search Bar ---

const DropdownWrapper: React.FC<{ 
  isOpen: boolean; 
  onClose: () => void; 
  children: React.ReactNode; 
  className?: string 
}> = ({ isOpen, onClose, children, className }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      ref={wrapperRef}
      className={`fixed inset-x-4 top-20 md:absolute md:inset-x-auto md:top-full md:left-0 mt-3 z-[100] bg-white rounded-lg shadow-[0_25px_60px_rgba(0,0,0,0.25)] border border-gray-100 animate-fade-in md:w-auto w-[calc(100vw-32px)] ${className}`}
    >
      {children}
    </div>
  );
};

const CalendarPicker: React.FC<{ onClose: () => void; onSelect: (val: string) => void }> = ({ onClose, onSelect }) => {
  const [activeTab, setActiveTab] = useState('period');
  const [viewDate, setViewDate] = useState(new Date(2026, 0, 1)); 
  const [startDate, setStartDate] = useState<Date | null>(new Date(2026, 0, 2)); 
  const [endDate, setEndDate] = useState<Date | null>(new Date(2026, 0, 16));    
  
  const days = ['PR', 'OT', 'TR', 'CE', 'PK', 'SE', 'SV'];
  const monthNames = [
    'Janvāris', 'Februāris', 'Marts', 'Aprīlis', 'Maijs', 'Jūnijs',
    'Jūlijs', 'Augusts', 'Septembris', 'Oktobris', 'Novembris', 'Decembris'
  ];

  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; 
  };

  const generateMonthData = (year: number, month: number) => {
    const totalDays = getDaysInMonth(year, month);
    const startOffset = getFirstDayOfMonth(year, month);
    const prevMonthDays = getDaysInMonth(year, month - 1);
    const grid = [];

    for (let i = startOffset - 1; i >= 0; i--) {
      grid.push({ date: new Date(year, month - 1, prevMonthDays - i), current: false });
    }
    for (let i = 1; i <= totalDays; i++) {
      grid.push({ date: new Date(year, month, i), current: true });
    }
    const remaining = 42 - grid.length;
    for (let i = 1; i <= remaining; i++) {
      grid.push({ date: new Date(year, month + 1, i), current: false });
    }
    return grid;
  };

  const handleDateClick = (date: Date) => {
    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
    } else {
      if (date < startDate) {
        setStartDate(date);
        setEndDate(null);
      } else {
        setEndDate(date);
      }
    }
  };

  const isInRange = (date: Date) => {
    return startDate && endDate && date > startDate && date < endDate;
  };

  const changeMonth = (offset: number) => {
    const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + offset, 1);
    setViewDate(newDate);
  };

  const renderMonth = (date: Date, showPrevBtn = false, showNextBtn = false) => {
    const monthData = generateMonthData(date.getFullYear(), date.getMonth());
    return (
      <div className="flex-1 w-full min-w-[280px]">
        <div className="flex items-center justify-center mb-6 relative">
          {showPrevBtn && (
            <button 
              type="button" 
              onClick={() => changeMonth(-1)}
              className="absolute left-0 w-8 h-8 rounded-md border border-gray-100 flex items-center justify-center text-gray-400 hover:bg-gray-50 transition-colors"
            >
              <i className="fas fa-chevron-left text-[10px]"></i>
            </button>
          )}
          <h4 className="text-sm md:text-base font-black text-[#05073C]">
            {monthNames[date.getMonth()]} {date.getFullYear()}
          </h4>
          {showNextBtn && (
            <button 
              type="button" 
              onClick={() => changeMonth(1)}
              className="absolute right-0 w-8 h-8 rounded-md border border-gray-100 flex items-center justify-center text-gray-400 hover:bg-gray-50 transition-colors"
            >
              <i className="fas fa-chevron-right text-[10px]"></i>
            </button>
          )}
        </div>
        <div className="grid grid-cols-7 mb-2">
          {days.map(d => <div key={d} className="text-[10px] font-black text-brand-deep/30 text-center py-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-y-1">
          {monthData.map((d, i) => {
            const start = startDate && d.date.getTime() === startDate.getTime();
            const end = endDate && d.date.getTime() === endDate.getTime();
            const range = isInRange(d.date);
            
            return (
              <div 
                key={i} 
                className={`relative text-[11px] md:text-[12px] font-bold py-2 text-center cursor-pointer transition-all rounded-md
                  ${!d.current ? 'text-brand-deep/10' : 'text-brand-deep hover:bg-orange-50 hover:text-brand-accent'}
                  ${start ? 'bg-[#05073C] text-white z-10 !text-white' : ''}
                  ${end ? 'bg-[#05073C] text-white z-10 !text-white' : ''}
                  ${range ? 'bg-orange-50 text-brand-accent' : ''}
                `}
                onClick={() => d.current && handleDateClick(d.date)}
              >
                {d.date.getDate()}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const secondMonthDate = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1);

  return (
    <div className="md:w-[780px] w-full max-h-[85vh] md:max-h-none overflow-y-auto custom-scrollbar overflow-x-hidden">
      {/* Tabs */}
      <div className="flex border-b border-gray-100">
        <button 
          type="button"
          onClick={() => setActiveTab('period')}
          className={`flex-1 py-4 text-[10px] md:text-[11px] font-black uppercase tracking-wider transition-all ${activeTab === 'period' ? 'text-brand-accent border-b-2 border-brand-accent' : 'text-brand-deep/40'}`}
        >
          Periods
        </button>
        <button 
          type="button"
          onClick={() => setActiveTab('exact')}
          className={`flex-1 py-4 text-[10px] md:text-[11px] font-black uppercase tracking-wider transition-all ${activeTab === 'exact' ? 'text-brand-accent border-b-2 border-brand-accent' : 'text-brand-deep/40'}`}
        >
          Datumi
        </button>
      </div>

      {/* Calendars */}
      <div className="p-4 md:p-10 flex flex-col md:flex-row gap-8 md:gap-16 relative">
        {renderMonth(viewDate, true, window.innerWidth < 768)}
        
        {/* Vertical Divider Line */}
        <div className="hidden md:block absolute left-1/2 top-10 bottom-10 w-[1px] bg-gray-100 -translate-x-1/2"></div>

        <div className="hidden md:block">
           {renderMonth(secondMonthDate, false, true)}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 md:p-6 flex flex-col md:flex-row justify-between items-center border-t border-gray-100 gap-4">
        <button 
          type="button"
          onClick={() => { setStartDate(null); setEndDate(null); onSelect(''); }}
          className="w-full md:w-auto px-8 py-3 text-xs md:text-sm font-bold text-brand-deep/60 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Dzēst visu
        </button>
        <button 
          type="button"
          onClick={() => {
            if (startDate) {
              const startStr = `${startDate.getDate()}. ${monthNames[startDate.getMonth()].slice(0, 3)}.`;
              const endStr = endDate ? ` - ${endDate.getDate()}. ${monthNames[endDate.getMonth()].slice(0, 3)}.` : '';
              onSelect(`${startStr}${endStr}`);
            }
            onClose();
          }}
          className="w-full md:w-auto px-10 md:px-14 py-4 bg-brand-accent hover:bg-brand-accentHover text-white font-black text-xs md:text-sm uppercase tracking-widest rounded-lg transition-all shadow-lg shadow-orange-100"
        >
          IZVĒLĒTIES
        </button>
      </div>
    </div>
  );
};

// --- Testimonial Card Component ---

const TestimonialCard: React.FC<{ 
  name: string; 
  location: string; 
  text: string; 
  image: string;
}> = ({ name, location, text, image }) => (
  <div className="bg-white p-6 md:p-10 rounded-lg border border-brand-surface shadow-sm hover:shadow-xl transition-all duration-500 flex flex-col h-full group">
    <div className="flex items-center gap-5 mb-6 md:mb-8">
      <div className="w-12 h-12 md:w-16 md:h-16 rounded-lg overflow-hidden border-2 border-brand-surface shadow-md flex-shrink-0">
        <img src={image} alt={name} className="w-full h-full object-cover" />
      </div>
      <div>
        <h4 className="text-sm md:text-base font-black text-brand-deep uppercase tracking-widest leading-tight">{name}</h4>
        <div className="flex items-center gap-2 mt-1">
          <i className="fas fa-map-marker-alt text-brand-accent text-xs"></i>
          <span className="text-[10px] md:text-xs font-bold text-brand-deep/40 uppercase tracking-widest">{location}</span>
        </div>
      </div>
    </div>
    
    <div className="flex gap-1 mb-4 md:mb-6">
      {[1,2,3,4,5].map(s => <i key={s} className="fas fa-star text-[#FFD700] text-sm md:text-base"></i>)}
    </div>

    <p className="text-brand-deep/80 text-base md:text-lg font-medium leading-relaxed italic flex-grow">
      "{text}"
    </p>

    <div className="mt-8 md:mt-10 pt-6 border-t border-brand-surface flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-green-500"></span>
        <span className="text-[9px] md:text-[10px] font-black text-brand-deep/30 uppercase tracking-[0.2em]">Pārbaudīts ceļotājs</span>
      </div>
      <i className="fas fa-quote-right text-brand-surface text-2xl md:text-3xl"></i>
    </div>
  </div>
);

// --- Bento Grid Component ---

const BentoTile: React.FC<{ 
  title: string; 
  image: string; 
  className?: string; 
  categoryName?: string;
}> = ({ title, image, className = "", categoryName }) => {
  const navigate = useNavigate();
  return (
    <div 
      onClick={() => navigate(`/celojumi?category=${encodeURIComponent(categoryName || title)}`)}
      className={`group relative overflow-hidden rounded-lg md:rounded-xl cursor-pointer shadow-xl transition-all duration-700 hover:-translate-y-2 ${className}`}
    >
      <img 
        src={image} 
        alt={title} 
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" 
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent group-hover:from-brand-accent/50 transition-all duration-500"></div>
      <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 right-4">
        <h3 className="text-lg md:text-2xl font-black text-white mb-3 md:mb-4 tracking-tight leading-tight drop-shadow-md">
          {title}
        </h3>
        <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg border border-white/50 flex items-center justify-center text-white transition-all duration-300 group-hover:bg-white group-hover:text-brand-accent group-hover:border-white">
          <i className="fas fa-arrow-right text-xs md:text-sm"></i>
        </div>
      </div>
    </div>
  );
};

// --- Main Home Component ---

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [search, setSearch] = useState({
    destination: '',
    dates: '',
    people: '2',
    nights: '7'
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const query = new URLSearchParams({
      destination: search.destination,
      people: search.people,
      nights: search.nights,
      dates: search.dates
    }).toString();
    navigate(`/celojumi?${query}`);
  };

  const topDestinations = COUNTRIES.slice(0, 4);
  const lastMinute = PACKAGES.filter(p => p.lastMinute).slice(0, 3);

  const testimonials = [
    {
      name: "Kristīne Ozola",
      location: "Dubaija, AAE",
      text: "LEC Travel komanda padarīja mūsu medusmēnesi Dubaijā vienkārši pasakainu. Katra detaļa bija pārdomāta līdz pēdējam sīkumam!",
      image: "https://i.pravatar.cc/150?u=kristine"
    },
    {
      name: "Artūrs Bērziņš",
      location: "Burgasa, Bulgārija",
      text: "Lieliska cena un tiešām individuāla pieeja. Pirmo reizi jutu, ka aģentūrai tiešām rūp mans miers un atpūta.",
      image: "https://i.pravatar.cc/150?u=arturs"
    },
    {
      name: "Līga Kalniņa",
      location: "Tenerife, Spānija",
      text: "Beidzot aģentūra, kurai var uzticēties. Viesnīca pārsniedza gaidīto, un transfērs bija precīzs un ērts. Iesaku!",
      image: "https://i.pravatar.cc/150?u=liga"
    },
    {
      name: "Jānis Vītols",
      location: "Antālija, Turcija",
      text: "Izcila atpūta visai ģimenei. 'Viss iekļauts' serviss bija augstākajā līmenī, un bērni bija sajūsmā par animatoriem.",
      image: "https://i.pravatar.cc/150?u=janis"
    },
    {
      name: "Ilze Lejiņa",
      location: "Grankanārija",
      text: "Mūžīgā pavasara sala mūs sagaidīja ar sauli. LEC Travel ieteiktā viesnīca bija tieši tas, ko meklējām mierīgai atpūtai.",
      image: "https://i.pravatar.cc/150?u=ilze"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1 >= testimonials.length ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 < 0 ? testimonials.length - 1 : prev - 1));
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative overflow-x-hidden">
      <div className="space-y-0">
        {/* Hero Section - MOBILE HEIGHT INCREASED TO PREVENT CUTOFF */}
        <section className="relative min-h-[950px] md:min-h-[700px] md:h-[850px] flex items-center justify-center pt-32 pb-48 md:pt-0 md:pb-0 z-20 overflow-visible">
          <div className="absolute inset-0 z-0 clip-bent-bottom bg-brand-deep">
            <img 
              src="https://images.unsplash.com/photo-1469474968028-56623f02e42e?auto=format&fit=crop&q=80&w=2000" 
              className="w-full h-full object-cover brightness-[0.4] scale-110"
              alt="Scenic Nature Travel"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-brand-deep/60 via-transparent to-brand-deep/80"></div>
          </div>

          <div className="relative z-20 text-center px-4 md:px-12 lg:px-20 w-full max-w-[1400px]">
            <h1 className="text-3xl md:text-7xl font-black mb-4 md:mb-6 animate-fade-in text-white tracking-tighter leading-tight md:leading-none uppercase">Piedzīvo pasauli</h1>
            <p className="text-base md:text-2xl mb-8 md:mb-12 text-white/90 max-w-2xl mx-auto font-medium px-4">Ar LEC Travel jūsu sapņu atpūta ir tieši viena klikšķa attālumā.</p>
            
            {/* SEARCH BAR - OPTIMIZED FOR MOBILE */}
            <div className="max-w-[1250px] mx-auto relative z-30">
              <form 
                onSubmit={handleSearch} 
                className="bg-white rounded-lg shadow-[0_40px_100px_rgba(0,0,0,0.3)] border border-white/20 p-4 md:p-5 flex flex-col md:flex-row items-center gap-4 relative z-40 animate-fade-in"
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6 flex-grow items-end text-left w-full">
                  {/* Galamērķis Segment */}
                  <div className="relative group w-full">
                    <label className="text-[9px] font-black text-brand-deep/30 uppercase tracking-widest ml-1 mb-2 block">Galamērķis</label>
                    <div 
                      onClick={() => setActiveDropdown(activeDropdown === 'destination' ? null : 'destination')}
                      className="flex items-center justify-between border-b border-gray-100 py-2.5 px-1 cursor-pointer hover:border-brand-accent transition-all"
                    >
                      <span className={`text-sm font-bold truncate ${search.destination ? 'text-brand-deep' : 'text-brand-deep/30'}`}>
                        {search.destination || 'Izvēlies valsti'}
                      </span>
                      <i className="fas fa-chevron-down text-[10px] text-brand-deep/20 group-hover:text-brand-accent"></i>
                    </div>
                    <DropdownWrapper isOpen={activeDropdown === 'destination'} onClose={() => setActiveDropdown(null)} className="w-full md:min-w-[320px]">
                      <div className="p-2 md:p-4 space-y-1 max-h-[300px] md:max-h-[350px] overflow-y-auto no-scrollbar">
                        {COUNTRIES.map(country => (
                          <div 
                            key={country.id} 
                            onClick={() => { setSearch({...search, destination: country.name}); setActiveDropdown(null); }}
                            className={`px-4 py-3 text-sm md:text-base font-bold rounded-lg cursor-pointer flex items-center gap-4 transition-all ${search.destination === country.name ? 'bg-brand-accent text-white shadow-lg' : 'text-brand-deep hover:bg-brand-surface'}`}
                          >
                             <i className={`fas fa-globe-europe text-xs ${search.destination === country.name ? 'opacity-100' : 'opacity-20'}`}></i> {country.name}
                          </div>
                        ))}
                      </div>
                    </DropdownWrapper>
                  </div>

                  {/* Datumi Segment */}
                  <div className="relative group w-full">
                    <label className="text-[9px] font-black text-brand-deep/30 uppercase tracking-widest ml-1 mb-2 block">Datumi</label>
                    <div 
                      onClick={() => setActiveDropdown(activeDropdown === 'dates' ? null : 'dates')}
                      className="flex items-center gap-3 border-b border-gray-100 py-2.5 px-1 cursor-pointer hover:border-brand-accent transition-all"
                    >
                      <i className="far fa-calendar text-brand-deep/20 text-xs"></i>
                      <span className={`text-sm font-bold truncate ${search.dates ? 'text-brand-deep' : 'text-brand-deep/30'}`}>
                        {search.dates || 'Izvēlies periodu'}
                      </span>
                      <i className="fas fa-chevron-down text-[10px] text-brand-deep/20 ml-auto group-hover:text-brand-accent"></i>
                    </div>
                    <DropdownWrapper isOpen={activeDropdown === 'dates'} onClose={() => setActiveDropdown(null)} className="w-full md:w-auto">
                      <CalendarPicker onClose={() => setActiveDropdown(null)} onSelect={(val) => setSearch({...search, dates: val})} />
                    </DropdownWrapper>
                  </div>

                  {/* Ceļotāji Segment */}
                  <div className="relative group w-full">
                    <label className="text-[9px] font-black text-brand-deep/30 uppercase tracking-widest ml-1 mb-2 block">Ceļotāji</label>
                    <div 
                      onClick={() => setActiveDropdown(activeDropdown === 'people' ? null : 'people')}
                      className="flex items-center gap-3 border-b border-gray-100 py-2.5 px-1 cursor-pointer hover:border-brand-accent transition-all"
                    >
                      <i className="fas fa-user-group text-brand-deep/20 text-xs"></i>
                      <span className="text-sm font-bold text-brand-deep">
                        {search.people}
                      </span>
                      <i className="fas fa-chevron-down text-[10px] text-brand-deep/20 ml-auto group-hover:text-brand-accent"></i>
                    </div>
                    <DropdownWrapper isOpen={activeDropdown === 'people'} onClose={() => setActiveDropdown(null)} className="w-full md:w-[180px]">
                       <div className="p-2 md:p-3 space-y-1">
                          {[1, 2, 3, 4, 5, 6].map(n => (
                            <div 
                              key={n} 
                              onClick={() => { setSearch({...search, people: n.toString()}); setActiveDropdown(null); }}
                              className={`px-4 py-2.5 text-sm md:text-base font-black rounded-lg cursor-pointer transition-all ${parseInt(search.people) === n ? 'bg-brand-accent text-white shadow-lg' : 'text-brand-deep hover:bg-brand-surface'}`}
                            >
                              {n} {n === 1 ? 'persona' : 'personas'}
                            </div>
                          ))}
                       </div>
                    </DropdownWrapper>
                  </div>

                  {/* Naktis Segment */}
                  <div className="relative group w-full">
                    <label className="text-[9px] font-black text-brand-deep/30 uppercase tracking-widest ml-1 mb-2 block">Naktis</label>
                    <div 
                      onClick={() => setActiveDropdown(activeDropdown === 'nights' ? null : 'nights')}
                      className="flex items-center gap-3 border-b border-gray-100 py-2.5 px-1 cursor-pointer hover:border-brand-accent transition-all"
                    >
                      <i className="far fa-moon text-brand-deep/20 text-xs"></i>
                      <span className="text-sm font-bold text-brand-deep">
                        {search.nights}...
                      </span>
                      <i className="fas fa-chevron-down text-[10px] text-brand-deep/20 ml-auto group-hover:text-brand-accent"></i>
                    </div>
                    <DropdownWrapper isOpen={activeDropdown === 'nights'} onClose={() => setActiveDropdown(null)} className="w-full md:w-[180px]">
                       <div className="p-2 md:p-3 space-y-1">
                          {[3, 5, 7, 10, 14, 21].map(n => (
                            <div 
                              key={n} 
                              onClick={() => { setSearch({...search, nights: n.toString()}); setActiveDropdown(null); }}
                              className={`px-4 py-2.5 text-sm md:text-base font-black rounded-lg cursor-pointer transition-all ${parseInt(search.nights) === n ? 'bg-brand-accent text-white shadow-lg' : 'text-brand-deep hover:bg-brand-surface'}`}
                            >
                              {n} naktis
                            </div>
                          ))}
                       </div>
                    </DropdownWrapper>
                  </div>
                </div>

                <button 
                  type="submit" 
                  className="w-full md:w-[200px] bg-brand-accent text-white h-[54px] rounded-lg font-bold text-sm uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-brand-accentHover transition-all shadow-lg active:scale-95 group/btn"
                >
                  MEKLĒT <i className="fas fa-search text-sm group-hover/btn:scale-110 transition-transform"></i>
                </button>
              </form>

              {/* TRUST/BENEFITS BAR */}
              <div className="mt-8 md:mt-14 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 animate-fade-in [animation-delay:300ms]">
                <div className="flex items-center gap-5 p-5 md:p-8 bg-black/25 backdrop-blur-xl rounded-xl border border-white/10 hover:bg-black/35 transition-all group text-left">
                  <div className="text-orange-400 group-hover:scale-110 transition-transform shrink-0">
                    <i className="far fa-clock text-2xl md:text-3xl"></i>
                  </div>
                  <div>
                    <h4 className="text-sm md:text-base font-black text-white mb-0.5">Rezervācija 24/7</h4>
                    <p className="text-[10px] font-bold text-white/50 leading-snug">Plānojiet savu sapņu atpūtu jebkurā laikā tiešsaistē.</p>
                  </div>
                </div>

                <div className="flex items-center gap-5 p-5 md:p-8 bg-black/25 backdrop-blur-xl rounded-xl border border-white/10 hover:bg-black/35 transition-all group text-left">
                  <div className="text-cyan-400 group-hover:scale-110 transition-transform shrink-0">
                    <i className="fas fa-shield-halved text-2xl md:text-3xl"></i>
                  </div>
                  <div>
                    <h4 className="text-sm md:text-base font-black text-white mb-0.5">Droši Partneri</h4>
                    <p className="text-[10px] font-bold text-white/50 leading-snug">Mēs sadarbojamies tikai ar labākajiem tūroperatoriem.</p>
                  </div>
                </div>

                <div className="flex items-center gap-5 p-5 md:p-8 bg-black/25 backdrop-blur-xl rounded-xl border border-white/10 hover:bg-black/35 transition-all group text-left">
                  <div className="text-pink-400 group-hover:scale-110 transition-transform shrink-0">
                    <i className="fas fa-heart text-2xl md:text-3xl"></i>
                  </div>
                  <div>
                    <h4 className="text-sm md:text-base font-black text-white mb-0.5">Individuāla pieeja</h4>
                    <p className="text-[10px] font-bold text-white/50 leading-snug">Katrs ceļojums tiek plānots atbilstoši Jūsu vēlmēm.</p>
                  </div>
                </div>
              </div>

              {/* Quick Search Tags - MOVED UP SLIGHTLY ON MOBILE AND ENSURED PARENT HEIGHT ACCOMMODATES THEM */}
              <div className="mt-12 md:mt-8 flex flex-wrap justify-center gap-3 animate-fade-in [animation-delay:600ms] opacity-0 [animation-fill-mode:forwards] pb-4">
                <span className="hidden md:inline text-[10px] font-black text-white/40 uppercase tracking-widest pt-1.5">Populāri:</span>
                {['Dubaija', 'Antālija', 'Burgasa', 'Tenerife'].map(city => (
                  <button 
                    key={city}
                    onClick={() => { setSearch({...search, destination: city}); navigate(`/celojumi?destination=${city}`); }}
                    className="bg-white/10 backdrop-blur-md text-white border border-white/20 px-4 py-1.5 rounded-full text-[9px] md:text-[10px] font-bold uppercase tracking-widest hover:bg-white hover:text-brand-deep transition-all"
                  >
                    {city}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Top Destinations */}
        <section className="relative py-12 md:py-20 bg-white z-10">
          <div className="absolute inset-0 bg-pattern-dots pointer-events-none"></div>
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 relative z-10">
            <div className="text-center mb-10 md:mb-12">
              <p className="text-brand-accent font-black uppercase text-[10px] md:text-xs tracking-[0.4em] mb-3">Atklāj jaunas vietas</p>
              <h2 className="text-2xl md:text-5xl font-black mb-4 text-brand-deep tracking-tighter">Top Galamērķi</h2>
              <div className="w-16 h-1 bg-brand-accent mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {topDestinations.map(c => (
                <div 
                  key={c.id} 
                  onClick={() => navigate(`/galamerki/${c.slug}`)}
                  className="group bg-white rounded-lg overflow-hidden cursor-pointer shadow-[0_20px_50px_-15px_rgba(5,7,60,0.08)] hover:shadow-[0_40px_80px_-15px_rgba(5,7,60,0.15)] transition-all duration-700 hover:-translate-y-3 border border-gray-50"
                >
                  <div className="relative h-[280px] md:h-[350px] overflow-hidden">
                    <img src={c.image} className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" alt={c.name} />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-deep/90 via-brand-deep/20 to-transparent opacity-80"></div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <h3 className="text-2xl md:text-3xl font-black text-white mb-1 tracking-tighter leading-none">{c.name}</h3>
                    </div>
                  </div>
                  <div className="p-5 md:p-6">
                     <p className="text-[9px] font-black uppercase tracking-[0.25em] text-brand-accent mb-3 text-left">Populārākie kūrorti:</p>
                     <div className="flex flex-wrap gap-2">
                        {c.cities.slice(0, 2).map(city => (
                          <span key={city.id} className="bg-brand-surface px-3 py-1.5 rounded-md text-[9px] font-black text-brand-deep">{city.name}</span>
                        ))}
                     </div>
                     <div className="mt-5 pt-4 border-t border-gray-50 flex items-center justify-between">
                        <span className="text-[9px] font-black text-brand-deep/30 uppercase tracking-widest">{c.cities.length} kūrorti</span>
                        <i className="fas fa-arrow-right text-brand-accent text-sm group-hover:translate-x-2 transition-transform"></i>
                     </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Last Minute Section */}
        <section className="relative py-16 md:py-28 bg-brand-surface clip-circle-top clip-circle-bottom z-10 -mt-12">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 relative z-10 pt-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-6 relative">
              <div className="max-w-3xl text-left relative z-20">
                <div className="inline-flex items-center gap-2 bg-brand-accent text-white px-4 py-2 rounded-md text-[9px] font-black uppercase tracking-[0.2em] mb-4 shadow-lg shadow-orange-100/40 animate-fade-in">
                  <i className="fas fa-clock animate-pulse"></i> Nepalaid garām
                </div>
                <h2 className="text-2xl md:text-5xl font-black mb-2 text-brand-deep tracking-tighter leading-tight">
                  Pēdējā brīža <span className="text-brand-accent">piedāvājumi</span>
                </h2>
              </div>
              <button 
                onClick={() => navigate('/celojumi?lastMinute=true')} 
                className="w-full md:w-auto group border-2 border-brand-deep/80 rounded-lg px-8 py-3.5 flex items-center justify-center gap-4 hover:bg-brand-deep hover:text-white transition-all duration-300 shadow-xl shadow-brand-deep/5 bg-white"
              >
                <span className="text-[11px] md:text-xs font-black uppercase tracking-[0.2em]">Skatīt visus</span>
                <i className="fas fa-arrow-right text-xs text-brand-accent group-hover:translate-x-2 transition-transform"></i>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {lastMinute.map(pkg => (
                <PackageCard key={pkg.id} pkg={pkg} />
              ))}
            </div>
          </div>
        </section>

        {/* About Us Summary */}
        <section className="py-12 md:py-24 relative bg-white overflow-hidden">
          <div className="absolute top-1/2 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-brand-deep/10 to-transparent"></div>
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 relative z-10">
            <div className="flex flex-col lg:flex-row items-center gap-12 md:gap-20">
              <div className="lg:flex-[1.3] space-y-6 md:space-y-8 order-2 lg:order-1 relative text-left">
                <span className="hidden md:block absolute -top-16 -left-8 text-[150px] font-black text-brand-deep/5 select-none -z-10 leading-none">01</span>
                <div className="inline-block bg-orange-50 text-brand-accent px-4 py-1.5 rounded-md text-[9px] font-black uppercase tracking-[0.3em] border border-orange-100">
                  Mūsu stāsts
                </div>
                <h2 className="text-2xl md:text-6xl font-black text-brand-deep tracking-tighter leading-tight">
                  LEC Travel – Tavs <br className="hidden md:block"/> <span className="text-brand-accent italic">uzticamais</span> ceļabiedrs
                </h2>
                <p className="text-base md:text-2xl text-brand-deep/80 leading-relaxed font-bold">
                  Mēs ticam, ka ceļošana nav lēmums, bet gan veids, kā ieraudzīt pasauli no jauna. Ar vairāk nekā 10 gadu pieredzi tūrisma nozarē, mēs nodrošinām tikai augstākās kvalitātes servisu.
                </p>
                <button 
                  onClick={() => navigate('/par-mums')}
                  className="w-full md:w-auto bg-brand-accent text-white px-10 py-5 rounded-lg font-black text-xs uppercase tracking-[0.2em] hover:bg-brand-accentHover transition-all shadow-2xl shadow-orange-100 flex items-center justify-center gap-4 group"
                >
                  Vairāk par mums
                  <i className="fas fa-arrow-right group-hover:translate-x-2 transition-transform text-base"></i>
                </button>
              </div>
              <div className="lg:flex-1 relative order-1 lg:order-2 w-full max-w-[320px] md:max-w-[450px] mx-auto">
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-brand-accent rounded-lg -z-10 opacity-30 animate-pulse-slow"></div>
                <div className="rounded-lg overflow-hidden shadow-[0_30px_60px_rgba(5,7,60,0.15)] border-[8px] md:border-[12px] border-white ring-1 ring-brand-surface bg-white transform hover:rotate-0 transition-transform duration-700 md:rotate-2">
                  <img src="https://images.unsplash.com/photo-1530789253388-582c481c54b0?q=80&w=1200&auto=format&fit=crop" alt="Ceļotāju komanda" className="w-full h-full object-cover aspect-[4/5]" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Category Bento Section */}
        <section className="relative py-16 md:py-24 bg-brand-surface clip-circle-top clip-circle-bottom z-10 -mt-12">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 relative z-10 pt-12">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5 md:h-[600px] h-auto">
              
              {/* Aktīvā atpūta - Tall Left */}
              <BentoTile 
                title="Aktīvā atpūta"
                image="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?q=80&w=800&auto=format&fit=crop"
                className="md:col-span-3 md:row-span-2 min-h-[250px] md:min-h-[300px]"
              />

              {/* Eksotiskie - Middle Top */}
              <BentoTile 
                title="Eksotiskie"
                categoryName="Eksotiskie ceļojumi"
                image="https://images.unsplash.com/photo-1544911845-1f34a3eb46b1?q=80&w=800&auto=format&fit=crop"
                className="md:col-span-3 md:row-span-1 min-h-[200px]"
              />

              {/* Viss iekļauts - Middle Right Top */}
              <BentoTile 
                title="Viss iekļauts"
                image="https://images.unsplash.com/photo-1540541338287-41700207dee6?q=80&w=800&auto=format&fit=crop"
                className="md:col-span-3 md:row-span-1 min-h-[200px]"
              />

              {/* Kombinētās tūres - Tall Right */}
              <BentoTile 
                title="Kombinētās tūres"
                image="https://images.unsplash.com/photo-1527631746610-bca00a040d60?q=80&w=800&auto=format&fit=crop"
                className="md:col-span-3 md:row-span-2 min-h-[250px] md:min-h-[300px]"
              />

              {/* Slēpošanas ceļojumi - Wide Middle Bottom */}
              <BentoTile 
                title="Slēpošanas ceļojumi"
                image="https://images.unsplash.com/photo-1551698618-1dfe5d97d256?q=80&w=1200&auto=format&fit=crop"
                className="md:col-span-6 md:row-span-1 min-h-[200px] md:min-h-[250px]"
              />
            </div>

            {/* Bottom Row - Smaller 3 Items */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mt-4 md:mt-5 h-auto md:h-[280px]">
               <BentoTile 
                title="Medusmēneša"
                categoryName="Medusmēneša ceļojumi"
                image="https://images.unsplash.com/photo-1583939003579-730e3918a45a?q=80&w=800&auto=format&fit=crop"
                className="min-h-[180px] md:min-h-[200px]"
              />
              <BentoTile 
                title="Darbatpūta"
                image="https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=800&auto=format&fit=crop"
                className="min-h-[180px] md:min-h-[200px]"
              />
              <BentoTile 
                title="Solo ceļojumi"
                image="https://images.unsplash.com/photo-1503220317375-aaad61436b1b?q=80&w=800&auto=format&fit=crop"
                className="min-h-[180px] md:min-h-[200px]"
              />
            </div>
          </div>
        </section>

        {/* Reviews Section */}
        <section className="relative py-16 md:py-24 bg-white text-brand-deep overflow-hidden">
          <div className="absolute inset-0 bg-pattern-dots opacity-5 pointer-events-none"></div>
          
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 relative z-10">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 md:mb-16 gap-6 md:gap-8 text-left">
              <div className="max-w-2xl">
                <div className="inline-block bg-brand-accent text-white px-4 py-2 rounded-md text-[9px] font-black uppercase tracking-[0.3em] mb-4 shadow-xl shadow-brand-accent/10">
                  Ceļotāju stāsti
                </div>
                <h2 className="text-3xl md:text-7xl font-black text-brand-deep tracking-tighter leading-tight">
                  Ko par mums saka <br className="hidden md:block"/><span className="text-brand-accent italic">klienti?</span>
                </h2>
              </div>
              
              <div className="flex items-center gap-5 md:gap-6 bg-white border border-gray-100 px-6 py-5 md:px-8 md:py-6 rounded-lg shadow-xl w-full md:w-auto">
                <div className="text-center border-r border-gray-100 pr-6 md:pr-8">
                  <div className="text-3xl md:text-5xl font-black text-brand-deep">4.9</div>
                  <div className="text-[9px] font-black text-brand-deep/30 uppercase tracking-widest mt-2">Reitings</div>
                </div>
                <div className="pl-4">
                  <div className="flex text-brand-accent text-xs md:text-lg mb-1.5">
                    {[1,2,3,4,5].map(s => <i key={s} className="fas fa-star"></i>)}
                  </div>
                  <div className="text-[9px] md:text-[10px] font-black text-brand-deep/40 uppercase tracking-widest">No 1,200+ atsauksmēm</div>
                </div>
              </div>
            </div>

            <div className="relative group">
              <div className="overflow-hidden">
                <div 
                  className="flex transition-transform duration-700 ease-in-out" 
                  style={{ transform: `translateX(-${currentSlide * (100 / (window.innerWidth >= 1024 ? 3 : 1))}%)` }}
                >
                  {testimonials.map((t, i) => (
                    <div key={i} className="w-full lg:w-1/3 flex-shrink-0 px-1 md:px-5">
                      <TestimonialCard {...t} />
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex justify-center md:block mt-8 md:mt-0">
                <button onClick={prevSlide} className="md:absolute left-[-20px] lg:left-[-60px] top-1/2 md:-translate-y-1/2 w-12 h-12 md:w-14 md:h-14 bg-white rounded-lg border border-gray-100 shadow-xl flex items-center justify-center text-brand-deep hover:text-brand-accent hover:border-brand-accent transition-all z-20 mx-2">
                  <i className="fas fa-chevron-left"></i>
                </button>
                <button onClick={nextSlide} className="md:absolute right-[-20px] lg:right-[-60px] top-1/2 md:-translate-y-1/2 w-12 h-12 md:w-14 md:h-14 bg-white rounded-lg border border-gray-100 shadow-xl flex items-center justify-center text-brand-deep hover:text-brand-accent hover:border-brand-accent transition-all z-20 mx-2">
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            </div>

            <div className="mt-12 md:mt-16 text-center">
              <div className="inline-flex items-center gap-4 px-5 py-3 bg-white rounded-lg border border-gray-100 shadow-sm">
                <div className="flex -space-x-3">
                  {[1,2,3,4].map(i => (
                    <img key={i} src={`https://i.pravatar.cc/100?u=user${i}`} className="w-8 h-8 rounded-lg border-2 border-white object-cover shadow-sm" alt="User avatar" />
                  ))}
                </div>
                <p className="text-[9px] md:text-[10px] font-black uppercase tracking-widest text-brand-deep/50">
                  Pievienojies vairāk nekā <span className="text-brand-deep">15,000</span> laimīgu ceļotāju
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export const CategoryCard: React.FC<{ label: string; image: string; className?: string; imgClassName?: string }> = ({ label, image, className, imgClassName }) => {
  const navigate = useNavigate();
  return (
    <div 
      onClick={() => navigate(`/celojumi?category=${encodeURIComponent(label)}`)}
      className={`flex flex-col items-center group cursor-pointer ${className}`}
    >
      <div className={`w-full overflow-hidden rounded-lg shadow-sm ${imgClassName}`}>
        <img 
          src={image} 
          alt={label} 
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
        />
      </div>
      <p className="mt-4 text-brand-deep font-black text-center text-sm md:text-base uppercase tracking-widest transition-colors group-hover:text-brand-accent">
        {label}
      </p>
    </div>
  );
};

export const PackageCard: React.FC<{ pkg: Package }> = ({ pkg }) => {
  const navigate = useNavigate();
  const oldPrice = Math.round(pkg.price * 1.25);
  
  const getShortMealPlan = (plan: string) => {
    if (plan.toLowerCase().includes('brokastis un vakariņas')) return 'HB';
    if (plan.toLowerCase().includes('viss iekļauts')) return 'AI';
    if (plan.toLowerCase().includes('tikai brokastis')) return 'BB';
    return plan;
  };

  return (
    <div 
      onClick={() => navigate(`/celojums/${pkg.slug}`)}
      className="bg-white rounded-lg overflow-hidden shadow-[0_20px_50px_rgba(5,7,60,0.05)] border border-gray-50 text-brand-deep cursor-pointer group hover:shadow-[0_40px_80px_rgba(5,7,60,0.12)] transition-all duration-500 flex flex-col h-full hover:-translate-y-2"
    >
      <div className="relative h-48 md:h-52 overflow-hidden flex-shrink-0">
        <img 
          src={`https://picsum.photos/seed/${pkg.slug}/800/600`} 
          className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" 
          alt={pkg.title} 
        />
        
        {pkg.lastMinute && (
          <div className="absolute top-3 left-3 z-20">
            <div className="bg-gradient-to-br from-[#FF822E] to-[#FF5C00] text-white px-3 py-1.5 rounded-md text-[9px] font-black uppercase tracking-widest shadow-xl border border-white/20 flex items-center gap-1.5 transform -rotate-1">
              <i className="fas fa-fire-alt animate-pulse"></i>
              Pēdējā brīža
            </div>
          </div>
        )}

        <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-md text-white px-3 py-1.5 rounded-md text-[9px] md:text-[10px] font-black uppercase tracking-widest">
          {pkg.nights} naktis
        </div>
      </div>

      <div className="p-5 md:p-6 flex flex-col flex-grow text-left">
        <div className="flex justify-between items-start mb-2 gap-3">
          <h3 className="text-lg md:text-xl font-black text-brand-deep group-hover:text-brand-accent transition-colors leading-tight line-clamp-2 tracking-tighter">
            {pkg.title}
          </h3>
          <div className="flex items-center gap-0.5 text-[#FFD700] text-[9px] mt-1 shrink-0">
            {Array.from({ length: pkg.hotel.stars }).map((_, i) => (
              <i key={i} className="fas fa-star"></i>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-1.5 mb-4 text-brand-deep/40">
          <i className="fas fa-map-marker-alt text-brand-accent text-xs"></i>
          <span className="text-[9px] md:text-[10px] font-black uppercase tracking-widest">{pkg.city}</span>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <span className="bg-brand-surface px-3 py-1 rounded-md text-[8px] md:text-[9px] font-black text-brand-deep/60 uppercase tracking-widest">
            {getShortMealPlan(pkg.mealPlan)}
          </span>
          <span className="bg-brand-surface px-3 py-1 rounded-md text-[8px] md:text-[9px] font-black text-brand-deep/60 uppercase tracking-widest">
            {pkg.hotel.facilities[0]}
          </span>
        </div>

        <div className="mt-auto pt-5 md:pt-6 border-t border-gray-50 flex items-end justify-between gap-4">
          <div className="shrink-0 text-left">
            <div className="text-gray-300 text-[10px] font-bold line-through mb-0.5">€{oldPrice}</div>
            <div className="text-xl md:text-2xl font-black text-brand-deep leading-none tracking-tighter">€{pkg.price}</div>
            <div className="text-brand-deep/30 text-[8px] font-black uppercase tracking-widest mt-1">par personu</div>
          </div>
          <button className="bg-brand-accent text-white px-5 py-3 rounded-lg md:py-3.5 font-black text-[9px] md:text-[10px] uppercase tracking-[0.2em] transition-all hover:bg-brand-accentHover shadow-xl shadow-orange-100/50 active:scale-95 whitespace-nowrap">
            APSKATĪT
          </button>
        </div>
      </div>
    </div>
  );
};
